import React, { useEffect, useRef } from "react";

// Cloudflare Turnstile site key (BBD-72). Site keys are public — committed
// here like the Firebase config in src/firebase.js. On localhost we use
// Cloudflare's documented "always passes" test key so the register flow can
// run in dev; the backend only accepts those tokens when it is configured
// with the matching test secret.
const TURNSTILE_TEST_SITE_KEY = "1x00000000000000000000AA";

const isLocalhost =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";

const TURNSTILE_SITE_KEY = isLocalhost
    ? TURNSTILE_TEST_SITE_KEY
    : "0x4AAAAAAEZdpBWDVORhHuaS";

const TURNSTILE_SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let turnstileScriptPromise = null;

function loadTurnstileScript() {
    if (window.turnstile) {
        return Promise.resolve();
    }
    if (!turnstileScriptPromise) {
        turnstileScriptPromise = new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = TURNSTILE_SCRIPT_SRC;
            script.async = true;
            script.onload = resolve;
            script.onerror = () => {
                turnstileScriptPromise = null;
                reject(new Error("Failed to load Turnstile script"));
            };
            document.head.appendChild(script);
        });
    }
    return turnstileScriptPromise;
}

// Renders the Cloudflare Turnstile checkbox and reports tokens up via
// onToken. Tokens are single-use and expire after a few minutes, so on
// expiry/error an empty token is reported, and bumping `resetSignal` (any
// changing number) forces a fresh token after a failed submit.
function TurnstileWidget({ siteKey = TURNSTILE_SITE_KEY, onToken, resetSignal = 0 }) {
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);
    const onTokenRef = useRef(onToken);

    useEffect(() => {
        onTokenRef.current = onToken;
    }, [onToken]);

    useEffect(() => {
        let cancelled = false;
        loadTurnstileScript()
            .then(() => {
                if (cancelled || !containerRef.current || widgetIdRef.current !== null) {
                    return;
                }
                widgetIdRef.current = window.turnstile.render(containerRef.current, {
                    sitekey: siteKey,
                    callback: (token) => onTokenRef.current(token),
                    "expired-callback": () => onTokenRef.current(""),
                    "error-callback": () => onTokenRef.current(""),
                });
            })
            .catch(() => onTokenRef.current(""));
        return () => {
            cancelled = true;
            if (widgetIdRef.current !== null && window.turnstile) {
                try {
                    window.turnstile.remove(widgetIdRef.current);
                } catch {
                    // widget already gone
                }
                widgetIdRef.current = null;
            }
        };
    }, [siteKey]);

    useEffect(() => {
        if (resetSignal && widgetIdRef.current !== null && window.turnstile) {
            onTokenRef.current("");
            window.turnstile.reset(widgetIdRef.current);
        }
    }, [resetSignal]);

    return (
        <div
            ref={containerRef}
            style={{ display: "flex", justifyContent: "center", marginBottom: "12px", minHeight: "65px" }}
        />
    );
}

export default TurnstileWidget;
